import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HeroScene } from "./HeroScene";
import { scrollToId } from "@/lib/scroll";
import { useBooking } from "@/lib/bookingStore";
import { heroProgress } from "@/lib/scrollState";

const LINES = [
    { text: "IDEAS ARE", outline: false },
    { text: "COMMON.", outline: true },
    { text: "EXECUTION", outline: false },
    { text: "IS RARE.", outline: true, accent: "RARE." },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ ready = true }) => {
    const openBooking = useBooking((s) => s.openBooking);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    useMotionValueEvent(scrollYProgress, "change", (v) => {
        heroProgress.value = v;
    });

    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-45%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
    const sceneY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col justify-end overflow-hidden"
        >
            <motion.div
                style={{ y: sceneY, scale: sceneScale }}
                className="absolute inset-0 opacity-60 lg:left-[42%] lg:opacity-100"
            >
                <HeroScene />
            </motion.div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/55 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 px-6 pb-10 pt-36 md:px-12 md:pb-14"
            >
            <motion.p
                data-testid="hero-overline"
                initial={{ opacity: 0, y: 16 }}
                animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
                className="font-mono-x mb-8 text-xs uppercase tracking-[0.25em] text-zinc-400"
            >
                [ Digital Studio — Tech × Marketing ]
            </motion.p>

            <h1 className="font-display font-black uppercase leading-[0.88] tracking-tighter text-[15vw] sm:text-[13vw] lg:text-[8.2vw]">
                {LINES.map((line, i) => (
                    <span key={line.text} className="block overflow-hidden pb-1">
                        <motion.span
                            className={`block ${line.outline ? "text-outline" : ""}`}
                            initial={{ y: "115%" }}
                            animate={ready ? { y: "0%" } : { y: "115%" }}
                            transition={{ duration: 1.1, ease: EASE, delay: 0.35 + i * 0.13 }}
                        >
                            {line.accent ? (
                                <>
                                    {line.text.replace(line.accent, "")}
                                    <span
                                        data-testid="hero-rare-accent"
                                        className="font-logo font-extrabold text-white [-webkit-text-stroke:0px]"
                                    >
                                        {line.accent}
                                    </span>
                                </>
                            ) : (
                                line.text
                            )}
                        </motion.span>
                    </span>
                ))}
            </h1>

            <div className="mt-10 flex flex-col gap-10 md:mt-14 md:flex-row md:items-end md:justify-between">
                <motion.p
                    data-testid="hero-subcopy"
                    initial={{ opacity: 0, y: 20 }}
                    animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
                    className="max-w-md text-base leading-relaxed text-zinc-400 md:text-lg"
                >
                    RaRe turns raw ideas into launched businesses — the website, the
                    marketing engine, and the automations that keep it all running.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 1.2 }}
                    className="flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-book-call-button"
                        onClick={openBooking}
                        className="group inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 font-display text-base font-bold text-[#050505] transition-transform duration-300 hover:scale-105 md:text-lg"
                    >
                        Book a Call
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-[#050505] text-white transition-transform duration-300 group-hover:rotate-45">
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                    </button>
                    <button
                        data-testid="hero-about-button"
                        onClick={() => scrollToId("#about")}
                        className="rounded-full border border-white/20 px-8 py-4 font-display text-base font-bold text-white transition-colors duration-300 hover:border-white/60 hover:bg-white/5 md:text-lg"
                    >
                        Why RaRe
                    </button>
                </motion.div>
            </div>

            <motion.div
                data-testid="hero-stats-strip"
                initial={{ opacity: 0 }}
                animate={ready ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 1, delay: 1.5 }}
                className="mt-14 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 md:grid-cols-4"
            >
                <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    12+ Yrs — Tech &amp; Marketing
                </p>
                <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Build · Launch · Market
                </p>
                <p className="font-mono-x hidden text-[11px] uppercase tracking-[0.2em] text-zinc-500 md:block">
                    Idea → Business, End-to-End
                </p>
                <p className="font-mono-x flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 md:justify-end">
                    Scroll
                    <motion.span
                        animate={{ y: [0, 5, 0] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <ArrowDown className="h-3.5 w-3.5" />
                    </motion.span>
                </p>
            </motion.div>
        </motion.div>
    </section>
    );
};
